import React, { useMemo } from 'react';
import { FormikProps } from 'formik';
import { Modal, Tabs } from 'antd';

import { FinancialInfoFormValues } from '../FinancialInfo';
import useFinancialInfoTabs, {
  FinancialInfoOptions,
  MultiRatesTypeEnum,
} from 'hooks/useFinancialInfoTabs';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  payCodesSelector,
  payRateTypesSelector,
} from 'redux/selectors/assignmentSelectors';
import { filterPayTypes } from 'utils/multiRates/multiRatesUtils';
import { getPayCodes } from 'features/assignment/AssignmentDetailsPage/utils';

import styles from './FinancialRatesSectionArray.module.less';

type Props = FinancialInfoOptions;

export default function FinancialRatesSectionArray(props: Props) {
  const { form } = props;
  const { values } = form as FormikProps<FinancialInfoFormValues>;
  const payRateTypes = useAppSelector(payRateTypesSelector);
  const payCodes = useAppSelector(payCodesSelector);
  const [tabs, handleAddTab, handleRemoveTab, activeTab, changeActiveTab] =
    useFinancialInfoTabs(props);

  const isScheduler = props?.mode === MultiRatesTypeEnum.Schedule;

  const payCodesList = useMemo(
    () => getPayCodes(payCodes, props?.selectedAssignmentType),
    [payCodes, props?.selectedAssignmentType],
  );
  const usedPayTypeIds = useMemo(
    () => values.multiRates?.map((rate) => rate.payTypeId),
    [values.multiRates],
  );

  const availableRateTypes = useMemo(
    () =>
      filterPayTypes({
        payRateTypes,
        payCodes: payCodesList,
        usedPayTypeIds,
      }),
    [payRateTypes, payCodesList, usedPayTypeIds],
  );

  const isAddBtnHidden = useMemo(() => {
    return isScheduler || availableRateTypes.length === 0;
  }, [isScheduler, availableRateTypes]);

  const handleFinancialInfoEdit = (
    targetKey: React.MouseEvent | React.KeyboardEvent | string,
    action: 'add' | 'remove',
  ) => {
    if (action === 'add') {
      handleAddTab();
    } else {
      Modal.confirm({
        title:
          'Please confirm the removal of Additional Rate. All data will be removed. Are you sure?',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          handleRemoveTab(targetKey);
        },
        onCancel() {},
      });
    }
  };

  return (
    <div className={styles.tabs}>
      <Tabs
        type={isScheduler ? 'card' : 'editable-card'}
        items={tabs}
        onEdit={handleFinancialInfoEdit}
        activeKey={activeTab}
        onChange={changeActiveTab}
        destroyInactiveTabPane={true}
        hideAdd={isAddBtnHidden}
      />
    </div>
  );
}
